import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { UserService } from '../../service/user.service';
import { IUserDTO } from '../../dto/user/user.dto';
import { USERNAME_REGEXP } from '../../validation/const';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import {
  NzResultComponent,
  NzResultExtraDirective,
} from 'ng-zorro-antd/result';
import {
  NzTabComponent,
  NzTabDirective,
  NzTabSetComponent,
} from 'ng-zorro-antd/tabs';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { InfoComponent } from './info/info.component';
import { StoriesComponent } from './stories/stories.component';
import { HighlightsComponent } from './highlights/highlights.component';
import { ReelsComponent } from './reels/reels.component';
import { PostsComponent } from './posts/posts.component';
import { AnalyticsService } from '../../service/analytics.service';
import { SentryService } from '../../service/sentry.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { MetaService } from '../../service/meta.service';
import { CloudflareTurnstileService } from '../../service/cloudflare-turnstile.service';
import { ConfigService } from '../../service/config.service';
import loadjs from 'loadjs';
import { unsubscribe } from '../../util/rx.util';
import { PageState } from '../../dto/enum/page-state.enum';
import { YaBannerService } from '../../service/ya-banner.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-profile-page',
  imports: [
    NzRowDirective,
    NzColDirective,
    NzSpinComponent,
    NzResultComponent,
    NzTabSetComponent,
    NzTabComponent,
    NzIconDirective,
    NzButtonComponent,
    NzResultExtraDirective,
    NzWaveDirective,
    RouterLink,
    InfoComponent,
    StoriesComponent,
    NzTabDirective,
    HighlightsComponent,
    ReelsComponent,
    PostsComponent,
  ],
  templateUrl: './profile-page.component.html',
  styleUrl: './profile-page.component.less',
})
export class ProfilePageComponent implements OnInit, OnDestroy {
  private static readonly CAPTCHA_ACTION: 'fetchUser' = 'fetchUser' as const;
  private static readonly SERVER_RESPONSES: Map<number, PageState> = new Map<
    number,
    PageState
  >([
    [400, PageState.VALIDATION_ERROR],
    [403, PageState.CAPTCHA_ERROR],
    [404, PageState.NOT_FOUND_ERROR],
  ]);
  private static readonly TAB_NAMES: Map<number, string> = new Map<
    number,
    string
  >([
    [0, 'stories'],
    [1, 'highlights'],
    [2, 'posts'],
    [2, 'reels'],
  ]);
  public readonly PageState: typeof PageState = PageState;
  public readonly userDTO: WritableSignal<IUserDTO | undefined> = signal<
    IUserDTO | undefined
  >(undefined);
  public readonly state: WritableSignal<PageState> = signal<PageState>(
    PageState.INIT
  );
  private readonly router: Router = inject(Router);
  private readonly metaService: MetaService = inject(MetaService);
  private readonly userService: UserService = inject(UserService);
  private readonly configService: ConfigService = inject(ConfigService);
  private readonly sentryService: SentryService = inject(SentryService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly analyticsService: AnalyticsService =
    inject(AnalyticsService);
  private readonly nzMessageService: NzMessageService =
    inject(NzMessageService);
  private readonly cloudflareTurnstileService: CloudflareTurnstileService =
    inject(CloudflareTurnstileService);
  private readonly yaBannerService: YaBannerService = inject(YaBannerService);
  private readonly document: Document = inject<Document>(DOCUMENT);
  private readonly platformId: object = inject(PLATFORM_ID);
  private captchaSub$?: Subscription = undefined;
  private userSub$?: Subscription = undefined;

  public ngOnInit(): void {
    this.activatedRoute.params.subscribe((params: Params): void => {
      const username: string = params['username'];
      if (!USERNAME_REGEXP.test(username)) {
        this.state.set(PageState.VALIDATION_ERROR);
        return;
      }
      this.metaService.setTitle(`${username}: Ig4u`);
      this.metaService.setDescription(
        $localize`:@@page-profile-description:Explore ${username}'s profile — stories, posts and reels using Ig4u service!`
      );
      this.metaService.setKeywords(
        $localize`:@@page-profile-keywords:${username}'s profile, ${username}'s stories, ${username}'s posts, ${username}'s reels, ig4u`
      );
      const uri: string = `profiles/${username}`;
      this.metaService.setCanonical(uri);
      this.metaService.setAlternatives(uri);
      this.metaService.disableIndexing();
      if (isPlatformBrowser(this.platformId)) {
        this.loadLightGalleryStyles();
        this.state.set(PageState.LOADING);
        this.captchaSub$ = this.cloudflareTurnstileService
          .execute(ProfilePageComponent.CAPTCHA_ACTION, username)
          .subscribe({
            next: (token: string): void => this.fetchUser(username, token),
            error: (error: unknown): void => {
              this.state.set(PageState.CAPTCHA_ERROR);
              console.error(
                'An error occurred during fetching captcha token',
                error
              );
            },
          });
      }
    });
  }

  public ngOnDestroy(): void {
    unsubscribe(this.userSub$);
    unsubscribe(this.captchaSub$);
  }

  public onSelectedTabIndexChanged(index: number): void {
    this.analyticsService.event('tab_exec', {
      name: ProfilePageComponent.TAB_NAMES.get(index) ?? 'unknown',
      username: this.userDTO()!.username,
      path: this.router.url,
    });
  }

  private fetchUser(username: string, captchaToken: string): void {
    this.userSub$ = this.userService
      .fetchUserByUsername(username, captchaToken)
      .subscribe({
        next: (userDTO: IUserDTO): void => {
          this.userDTO.set(userDTO);
          this.state.set(PageState.OK);
          this.yaBannerService.push(
            'R-A-15626278-7',
            'yandex_rtb_R-A-15626278-7',
            'banner'
          );
          this.yaBannerService.push(
            'R-A-15626278-8',
            'yandex_rtb_R-A-15626278-8',
            'feed'
          );
          this.analyticsService.event('tab_exec', {
            name: ProfilePageComponent.TAB_NAMES.get(0) ?? 'unknown',
            username: this.userDTO()!.username,
            path: this.router.url,
          });
        },
        error: (error: HttpErrorResponse): void => {
          this.state.set(
            ProfilePageComponent.SERVER_RESPONSES.get(error.status) ??
              PageState.UNEXPECTED_ERROR
          );
          console.error(
            `An error occurred during fetching user with username [${username}]`,
            error
          );
          if (![403, 404].includes(error.status)) {
            this.sentryService.captureException({
              error,
              severityLevel: 'error',
              transactionName: 'FETCH_USER_ERROR',
              user: { username },
            });
          }
        },
      });
  }

  private loadLightGalleryStyles(): void {
    if (this.document.getElementById('lightgallery')) {
      return;
    }

    loadjs('lightgallery.css', {
      async: true,
      numRetries: this.configService.retryCount,
      error: (): void => {
        this.nzMessageService.error(
          $localize`:@@light-gallery-styles-error:An error occurred during loading LightGallery styles`
        );
        console.error('An error occurred during loading LightGallery styles');
        this.sentryService.captureException({
          error: 'An error occurred during loading LightGallery styles',
          severityLevel: 'error',
          transactionName: 'LOAD_LIGHT_GALLERY_STYLES_ERROR',
        });
      },
      before: (path: string, scriptEl: HTMLElement): boolean => {
        const htmlElement: HTMLElement | null =
          this.document.getElementById('lightgallery');
        if (htmlElement) {
          htmlElement.parentElement?.removeChild(htmlElement);
        }
        scriptEl.id = 'lightgallery';
        return true;
      },
    });
  }
}
